import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Pencil, Trash2, FileText, Check } from 'lucide-react';
import { Condition, CONDITION_LABELS } from '@crosova/shared';
import { api } from '../../lib/api';

interface Template {
  id: string;
  name: string;
  description?: string;
  category?: string;
  subcategory?: string;
  brand?: string;
  colour?: string;
  size?: string;
  condition?: string;
  price?: number;
  tags: string[];
  folder?: string;
}

const EMPTY: Omit<Template, 'id'> = {
  name: '', description: '', category: '', subcategory: '',
  brand: '', colour: '', size: '', condition: '', price: undefined, tags: [], folder: '',
};

export function TemplatesPage() {
  const qc = useQueryClient();
  const [editing, setEditing] = useState<Template | Omit<Template, 'id'> | null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);

  const { data: templates = [], isLoading } = useQuery<Template[]>({
    queryKey: ['templates'],
    queryFn: () => api.get('/templates').then((r) => r.data),
  });

  const save = useMutation({
    mutationFn: (t: Omit<Template, 'id'> & { id?: string }) =>
      t.id ? api.patch(`/templates/${t.id}`, t) : api.post('/templates', t),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['templates'] }); setEditing(null); },
  });

  const del = useMutation({
    mutationFn: (id: string) => api.delete(`/templates/${id}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['templates'] }); setDeleteId(null); },
  });

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-base font-semibold tracking-tight">Templates</h1>
          <p className="text-[11.5px] text-white/25 mt-0.5">Save common item details so you can fill in new listings faster</p>
        </div>
        <button onClick={() => setEditing(EMPTY)} className="btn-primary flex items-center gap-1.5">
          <Plus size={13} /> New template
        </button>
      </div>

      {isLoading ? (
        <div className="p-10 text-center text-white/30 text-sm">Loading...</div>
      ) : templates.length === 0 ? (
        <div className="card p-12 flex flex-col items-center text-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-white/[0.04] border border-white/[0.06] flex items-center justify-center">
            <FileText size={18} className="text-white/20" />
          </div>
          <div>
            <p className="text-sm text-white/40">No templates yet</p>
            <p className="text-xs text-white/20 mt-1">Create one to speed up listing similar items</p>
          </div>
          <button onClick={() => setEditing(EMPTY)} className="btn-primary mt-1">Create first template</button>
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-3">
          {templates.map((t) => (
            <div key={t.id} className="card p-4 flex flex-col gap-2">
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] font-semibold truncate">{t.name}</div>
                  {t.description && <p className="text-[11px] text-white/35 mt-0.5 line-clamp-2">{t.description}</p>}
                </div>
                <div className="flex gap-1 flex-shrink-0">
                  <button onClick={() => setEditing(t)} className="w-6 h-6 rounded flex items-center justify-center text-white/30 hover:text-white/70 hover:bg-white/[0.05] transition-colors">
                    <Pencil size={12} />
                  </button>
                  <button onClick={() => setDeleteId(t.id)} className="w-6 h-6 rounded flex items-center justify-center text-white/30 hover:text-danger hover:bg-danger/[0.06] transition-colors">
                    <Trash2 size={12} />
                  </button>
                </div>
              </div>
              <div className="flex flex-wrap gap-1">
                {t.brand && <Chip>{t.brand}</Chip>}
                {t.size && <Chip>{t.size}</Chip>}
                {t.condition && <Chip>{CONDITION_LABELS[t.condition as Condition] ?? t.condition}</Chip>}
                {t.category && <Chip>{t.category}</Chip>}
                {t.price && <Chip secondary>£{(t.price / 100).toFixed(2)}</Chip>}
                {t.tags.map((tag) => <Chip key={tag}>#{tag}</Chip>)}
              </div>
            </div>
          ))}
        </div>
      )}

      {editing !== null && (
        <TemplateModal
          template={editing}
          onSave={(data) => save.mutate(data as any)}
          onClose={() => setEditing(null)}
          loading={save.isPending}
        />
      )}

      {deleteId && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-bg-surface border border-white/[0.08] rounded-2xl w-full max-w-sm p-5">
            <h2 className="text-sm font-semibold mb-1">Delete template?</h2>
            <p className="text-[11px] text-white/40 mb-5">This won't affect any existing listings.</p>
            <div className="flex gap-2">
              <button onClick={() => setDeleteId(null)} className="btn-secondary flex-1">Cancel</button>
              <button
                onClick={() => del.mutate(deleteId)}
                disabled={del.isPending}
                className="flex-1 px-3 py-1.5 rounded-lg text-xs font-medium bg-danger/80 hover:bg-danger text-white transition-colors"
              >
                {del.isPending ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function TemplateModal({
  template, onSave, onClose, loading,
}: {
  template: Template | Omit<Template, 'id'>;
  onSave: (t: any) => void;
  onClose: () => void;
  loading: boolean;
}) {
  const [form, setForm] = useState({ ...template });
  const [tagInput, setTagInput] = useState('');

  function set<K extends keyof typeof form>(key: K, value: (typeof form)[K]) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  function addTag() {
    const tag = tagInput.trim().replace(/^#/, '');
    if (tag && !form.tags.includes(tag)) set('tags', [...form.tags, tag]);
    setTagInput('');
  }

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-bg-surface border border-white/[0.08] rounded-2xl w-full max-w-lg p-6 my-4">
        <h2 className="text-sm font-semibold mb-4">{'id' in form ? 'Edit template' : 'New template'}</h2>

        <div className="space-y-3">
          <Field label="Template name *">
            <input className="input" value={form.name} onChange={(e) => set('name', e.target.value)} placeholder="e.g. Nike Trainers" />
          </Field>

          <Field label="Description (starter text)">
            <textarea className="input resize-none h-16 text-[11px]" value={form.description ?? ''} onChange={(e) => set('description', e.target.value)} placeholder="Good condition, no major defects..." />
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Category">
              <input className="input" value={form.category ?? ''} onChange={(e) => set('category', e.target.value)} />
            </Field>
            <Field label="Sub-category">
              <input className="input" value={form.subcategory ?? ''} onChange={(e) => set('subcategory', e.target.value)} />
            </Field>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <Field label="Brand">
              <input className="input" value={form.brand ?? ''} onChange={(e) => set('brand', e.target.value)} />
            </Field>
            <Field label="Colour">
              <input className="input" value={form.colour ?? ''} onChange={(e) => set('colour', e.target.value)} />
            </Field>
            <Field label="Size">
              <input className="input" value={form.size ?? ''} onChange={(e) => set('size', e.target.value)} />
            </Field>
          </div>

          <Field label="Condition">
            <div className="flex gap-1.5 flex-wrap">
              {(['', ...Object.values(Condition)] as string[]).map((c) => (
                <button
                  key={c}
                  onClick={() => set('condition', c || undefined)}
                  className={`px-2 py-1 rounded text-[10px] border transition-colors ${
                    (form.condition ?? '') === c
                      ? 'border-primary-500/40 bg-primary-500/10 text-primary-300'
                      : 'border-white/[0.07] bg-white/[0.02] text-white/30'
                  }`}
                >
                  {c ? CONDITION_LABELS[c as Condition] : 'Any'}
                </button>
              ))}
            </div>
          </Field>

          <Field label="Default price hint (£)">
            <input
              className="input"
              type="number"
              min="0"
              step="0.01"
              value={form.price ? (form.price / 100).toFixed(2) : ''}
              onChange={(e) => set('price', e.target.value ? Math.round(parseFloat(e.target.value) * 100) : undefined)}
              placeholder="Leave blank to set per-listing"
            />
          </Field>

          <Field label="Default tags">
            <div className="flex gap-1.5 flex-wrap mb-1.5">
              {form.tags.map((tag) => (
                <span key={tag} className="flex items-center gap-1 bg-primary-500/[0.08] border border-primary-500/20 text-primary-300 text-[10px] px-1.5 py-0.5 rounded">
                  #{tag}
                  <button onClick={() => set('tags', form.tags.filter((t) => t !== tag))} className="text-white/30 hover:text-white/70">×</button>
                </span>
              ))}
            </div>
            <div className="flex gap-1.5">
              <input
                className="input flex-1 text-[11px]"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                placeholder="Add tag and press Enter"
              />
              <button onClick={addTag} className="btn-secondary !py-1 !px-3 text-[11px]">Add</button>
            </div>
          </Field>
        </div>

        <div className="flex gap-2 mt-5">
          <button onClick={onClose} className="btn-secondary flex-1">Cancel</button>
          <button
            onClick={() => onSave(form)}
            disabled={!form.name.trim() || loading}
            className="btn-primary flex-1 flex items-center justify-center gap-1.5"
          >
            {loading ? 'Saving...' : <><Check size={12} /> Save template</>}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="text-[10px] font-medium text-white/35 mb-1">{label}</div>
      {children}
    </div>
  );
}

function Chip({ children, secondary }: { children: React.ReactNode; secondary?: boolean }) {
  return (
    <span className={`text-[9.5px] px-1.5 py-0.5 rounded border ${secondary ? 'bg-secondary-400/[0.07] border-secondary-400/20 text-secondary-400/70' : 'bg-white/[0.04] border-white/[0.07] text-white/35'}`}>
      {children}
    </span>
  );
}
